/* Windows 98-style media player: playlist playback, custom controls, and a
   debug tool to map the video/title/timer safe areas by dragging boxes. */
class MediaPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('.media-player__video');
    this.audio = this.querySelector('.media-player__audio');
    this.titleEl = this.querySelector('[data-title]');
    this.timerEl = this.querySelector('[data-timer]');
    this.playlist = this.readPlaylist();
    this.index = 0;
    // Net next(+1)/prev(-1) steps tapped while a load is in flight, applied
    // once the load resolves so rapid taps aren't dropped.
    this.pendingStep = 0;

    this.buttons = {};
    this.querySelectorAll('.mp-button').forEach((b) => {
      this.buttons[b.dataset.action] = b;
      this.bindButton(b);
    });

    if (this.video) {
      this.video.addEventListener('timeupdate', () => this.renderTimer());
      this.video.addEventListener('loadedmetadata', () => this.renderTimer());
      this.video.addEventListener('ended', () => this.onVideoEnded());
      this.video.addEventListener('play', () => this.reflectPlayState());
      this.video.addEventListener('pause', () => this.reflectPlayState());
    }

    if (this.audio) {
      // When there's a song, the audio track owns timing and end-of-track.
      this.audio.addEventListener('timeupdate', () => this.renderTimer());
      this.audio.addEventListener('loadedmetadata', () => this.renderTimer());
      this.audio.addEventListener('ended', () => this.onAudioEnded());
    }

    this.load(0, false);
    this.reflectMuteState();
    this.reflectPlayState();

    // No autoplay: browsers block audio from starting without a user gesture,
    // so the player starts paused and waits for the play button.

    if (this.hasAttribute('data-debug')) this.initDebug();
  }

  // Wire up a control button. We drive the action from pointerup rather than
  // the synthetic `click`: on iOS a light/quick tap fires :active but the
  // browser often decides the touch wasn't a full click and never dispatches
  // one, so the button visibly depresses but nothing happens (a harder/longer
  // tap was needed). pointerup fires reliably for any real tap. Keyboard users
  // still get `click` from Enter/Space, so that path is kept but de-duped.
  bindButton(b) {
    const action = b.dataset.action;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    b.addEventListener('pointerdown', (e) => {
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    });

    b.addEventListener('pointerup', (e) => {
      if (!tracking) return;
      tracking = false;
      // Ignore if the finger moved enough to read as a scroll/drag, not a tap.
      const moved = Math.abs(e.clientX - startX) > 12 || Math.abs(e.clientY - startY) > 12;
      if (moved) return;
      // Suppress the synthetic click that follows a touch so we don't fire twice.
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 400);
      this.onAction(action);
    });

    b.addEventListener('pointercancel', () => (tracking = false));

    // Keyboard (Enter/Space) and any environment without pointer events still
    // dispatch a real click; honor it unless it's the touch-synthesized one.
    b.addEventListener('click', () => {
      if (this.suppressClick) return;
      this.onAction(action);
    });
  }

  // Whether the current track has its own audio file driving sound/timing.
  get hasAudio() {
    return !!(this.audio && this.audio.src);
  }

  readPlaylist() {
    const el = this.querySelector('[data-playlist]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent).filter((t) => t.src);
    } catch (e) {
      return [];
    }
  }

  load(i, autoplay) {
    if (!this.playlist.length || !this.video) return;
    this.index = (i + this.playlist.length) % this.playlist.length;
    const track = this.playlist[this.index];

    // Loading a source is async (network fetch + decode). Show a loading state
    // and disable the transport controls until the media is ready to play.
    this.setLoading(true);

    this.video.src = track.src;
    if (track.poster) this.video.poster = track.poster;
    if (this.titleEl) this.titleEl.textContent = track.title || '';

    // When a song's audio track is present it's usually longer than the visual
    // video, so loop the video to keep it playing underneath the song. Without
    // audio, the video owns end-of-track and must not loop (so 'ended' fires).
    this.video.loop = !!track.audio;
    this.video.load();

    // Load this song's audio track, if any. The video stays muted; sound and
    // timing come from the audio element.
    if (this.audio) {
      if (track.audio) {
        this.audio.src = track.audio;
        this.audio.load();
      } else {
        this.audio.removeAttribute('src');
        this.audio.load();
      }
    }

    // Clear the loading state once the media that owns timing can play. Use the
    // audio track if present, otherwise the video. Errors also clear it so the
    // controls never stay stuck disabled.
    const owner = track.audio && this.audio ? this.audio : this.video;
    const done = () => this.setLoading(false);
    owner.addEventListener('canplay', done, { once: true });
    owner.addEventListener('error', done, { once: true });

    this.renderTimer();
    if (autoplay) this.play();
  }

  // Toggle the loading state. Marks the host so CSS can show a spinner/dimmed
  // state via [data-loading]. We deliberately do NOT set the buttons' `disabled`
  // attribute: a disabled button swallows clicks in the browser before any JS
  // runs, so rapid next/prev taps during a load would be lost. Instead the
  // buttons stay clickable and onAction queues taps that arrive mid-load.
  setLoading(on) {
    this.loading = on;
    this.toggleAttribute('data-loading', on);
    // If a load just finished and the user tapped next/prev while it was in
    // flight, apply that queued step now instead of dropping it.
    if (!on && this.pendingStep) {
      const step = this.pendingStep;
      this.pendingStep = 0;
      this.load(this.index + step, !this.video.paused || this.started);
    }
  }

  onAction(action) {
    switch (action) {
      case 'playpause':
        // playpause toggles the current element; ignore it mid-load so we don't
        // toggle against a source that isn't ready yet.
        if (this.loading) return;
        this.video.paused ? this.play() : this.pause();
        break;
      case 'next':
        // Mid-load, remember the intent and apply it when the load resolves,
        // so fast taps advance the right number of tracks instead of being lost.
        if (this.loading) {
          this.pendingStep += 1;
          return;
        }
        this.load(this.index + 1, !this.video.paused || this.started);
        break;
      case 'prev':
        if (this.loading) {
          this.pendingStep -= 1;
          return;
        }
        // Restart current track if we're past the first couple seconds.
        if (this.video.currentTime > 2) {
          this.video.currentTime = 0;
          if (this.hasAudio) this.audio.currentTime = 0;
        } else {
          this.load(this.index - 1, !this.video.paused || this.started);
        }
        break;
      case 'mute':
        // The video is always muted; muting toggles the audio track.
        if (this.audio) this.audio.muted = !this.audio.muted;
        this.reflectMuteState();
        break;
    }
  }

  play() {
    this.started = true;
    // Keep audio aligned to the (visual) video before starting both.
    if (this.hasAudio) {
      this.audio.currentTime = this.video.currentTime;
      const ap = this.audio.play();
      // If audio autoplay is blocked (no user gesture yet), retry on the first
      // interaction so sound joins the already-playing video.
      if (ap && ap.catch) ap.catch(() => this.armGestureUnlock());
    }
    const p = this.video.play();
    if (p && p.catch) p.catch(() => {});
    this.reflectPlayState();
  }

  // Start audio on the first user gesture after a blocked autoplay attempt.
  armGestureUnlock() {
    if (this.gestureArmed) return;
    this.gestureArmed = true;
    const unlock = () => {
      this.gestureArmed = false;
      remove();
      // Only resume audio if the user hasn't since paused the player.
      if (this.hasAudio && !this.video.paused) {
        this.audio.currentTime = this.video.currentTime;
        const ap = this.audio.play();
        if (ap && ap.catch) ap.catch(() => {});
      }
    };
    const events = ['pointerdown', 'touchstart', 'keydown'];
    const remove = () =>
      events.forEach((e) => window.removeEventListener(e, unlock));
    events.forEach((e) =>
      window.addEventListener(e, unlock, { once: true, passive: true })
    );
  }

  pause() {
    this.video.pause();
    if (this.audio) this.audio.pause();
  }

  // Video ended: if there's an audio track it owns end-of-track, so ignore.
  onVideoEnded() {
    if (this.hasAudio) return;
    this.advance();
  }

  // Audio ended: it's the song, so advance regardless of the (looping) video.
  onAudioEnded() {
    this.advance();
  }

  advance() {
    const last = this.index === this.playlist.length - 1;
    if (last && this.video.hasAttribute('data-loop-last')) {
      this.load(this.index, true);
    } else {
      this.load(this.index + 1, true);
    }
  }

  reflectPlayState() {
    const b = this.buttons.playpause;
    const playing = !!(this.video && !this.video.paused);
    if (b) b.toggleAttribute('data-toggled', playing);
  }

  reflectMuteState() {
    const b = this.buttons.mute;
    // Reflects the audio track's mute state (video is always muted).
    const muted = this.audio ? this.audio.muted : true;
    if (b) b.toggleAttribute('data-toggled', muted);
  }

  renderTimer() {
    if (!this.timerEl) return;
    // Prefer the audio track's clock when present — it's the real song length.
    const media = this.hasAudio ? this.audio : this.video;
    const cur = this.fmt(media.currentTime);
    const dur = this.fmt(media.duration);
    this.timerEl.textContent = `${cur}/${dur}`;
  }

  fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  /* ---------------- Debug mapping tool ---------------- */
  initDebug() {
    const frame = this.querySelector('.media-player__frame');
    const readout = this.querySelector('[data-debug-readout]');
    const coordsEl = this.querySelector('[data-debug-coords]');
    const copyBtn = this.querySelector('[data-debug-copy]');
    if (readout) readout.hidden = false;

    const areas = {
      video: this.querySelector('[data-area="video"]'),
      title: this.querySelector('[data-area="title"]'),
      timer: this.querySelector('[data-area="timer"]'),
    };

    const settingId = { video: 'video', title: 'title', timer: 'timer' };

    const pct = (px, total) => Math.max(0, Math.min(100, (px / total) * 100));

    const render = () => {
      let out = '';
      for (const [name, el] of Object.entries(areas)) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const box = frame.getBoundingClientRect();
        const round = (n) => Math.round(n * 10) / 10;
        const x = round(pct(rect.left - box.left, box.width));
        const y = round(pct(rect.top - box.top, box.height));
        const w = round(pct(rect.width, box.width));
        const h = round(pct(rect.height, box.height));
        const id = settingId[name];
        out += `${name}:  x ${x}  y ${y}  w ${w}  h ${h}\n`;
        out += `  ${id}_x=${x}  ${id}_y=${y}  ${id}_w=${w}  ${id}_h=${h}\n`;
      }
      if (coordsEl) coordsEl.textContent = out.trim();
    };

    let active = null;
    let mode = null; // 'move' | 'resize'
    let start = null;

    const onDown = (e, el) => {
      e.preventDefault();
      const box = frame.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const nearHandle =
        e.clientX > rect.right - 22 && e.clientY > rect.bottom - 22;
      active = el;
      mode = nearHandle ? 'resize' : 'move';
      el.setPointerCapture?.(e.pointerId);
      start = {
        px: e.clientX,
        py: e.clientY,
        left: rect.left - box.left,
        top: rect.top - box.top,
        width: rect.width,
        height: rect.height,
        boxW: box.width,
        boxH: box.height,
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    const onMove = (e) => {
      if (!active) return;
      const dx = e.clientX - start.px;
      const dy = e.clientY - start.py;
      if (mode === 'move') {
        const left = Math.max(0, Math.min(start.boxW - start.width, start.left + dx));
        const top = Math.max(0, Math.min(start.boxH - start.height, start.top + dy));
        active.style.left = pct(left, start.boxW) + '%';
        active.style.top = pct(top, start.boxH) + '%';
      } else {
        const width = Math.max(20, Math.min(start.boxW - start.left, start.width + dx));
        const height = Math.max(12, Math.min(start.boxH - start.top, start.height + dy));
        active.style.width = pct(width, start.boxW) + '%';
        active.style.height = pct(height, start.boxH) + '%';
      }
      render();
    };

    const onUp = () => {
      active = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    for (const el of Object.values(areas)) {
      if (el) el.addEventListener('pointerdown', (e) => onDown(e, el));
    }

    if (copyBtn && coordsEl) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard?.writeText(coordsEl.textContent);
        const orig = copyBtn.textContent;
        copyBtn.textContent = '✓';
        setTimeout(() => (copyBtn.textContent = orig), 1200);
      });
    }

    render();
    window.addEventListener('resize', render);
  }
}

customElements.define('media-player', MediaPlayer);
